import React, {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import countries from 'i18n-iso-countries';
import { i18n } from '@lingui/core';
import { I18nProvider as LinguiProvider } from '@lingui/react';
import { HeadFC, HeadProps } from 'gatsby';
import { Language, NormalisedLocale } from '@/types/common';
import { getUserFromStorage } from '@/utils/auth';
import {
  defaultLocale,
  getLanguageComponent,
  getLocaleForLanguage,
  isKnownLanguage,
  languageNames,
} from '@/utils/i18n';
import { read, write } from '@/utils/sessionStorage';
import { activate, importLocale } from './utils';

type I18nContext = {
  locale: NormalisedLocale;
  currentLanguageName: string;
  language: string;
  languages: Record<NormalisedLocale, string>;
  activate: (locale: NormalisedLocale) => void;
};

const languages: Record<NormalisedLocale, string> = {
  'en-GB': languageNames.en,
  'fr-FR': languageNames.fr,
  'de-DE': languageNames.de,
  'es-ES': languageNames.es,
  'it-IT': languageNames.it,
  'ja-JP': languageNames.ja,
} as const;

const Context = createContext<I18nContext>({
  language: '',
  currentLanguageName: '',
  locale: defaultLocale,
  languages,
  activate: () => {},
});

export const useI18n = () => useContext(Context);

type Props = PropsWithChildren<{
  locale?: NormalisedLocale;
  onActivate?: (locale: NormalisedLocale) => void;
}>;

const getSessionLocale = () =>
  read<NormalisedLocale | undefined>('localStorage', 'preferred_locale');

const getUserLocale = () => {
  if (typeof window !== 'undefined') {
    const userLang = getUserFromStorage()?.languageId as Language | undefined;
    const browserLang = navigator.languages
      .map(getLanguageComponent)
      .filter(isKnownLanguage)?.[0];
    const lang = userLang ?? browserLang;
    return lang ? getLocaleForLanguage(lang) : undefined;
  }
};

const I18nProvider: FC<Props> = ({ children, onActivate, locale }) => {
  const initialLocale = locale ?? getSessionLocale() ?? getUserLocale() ?? defaultLocale;
  const [currentLocale, setCurrentLocale] = useState<NormalisedLocale>(initialLocale);
  const currentLanguage = currentLocale.split('-')[0];

  if (typeof window === 'undefined' && currentLocale === locale) {
    const lang = require(`!!@lingui/loader!@/locales/${locale}.po`);
    i18n.load(locale, lang.messages);
    i18n.activate(locale);
  }

  useEffect(() => {
    const lang = require(`!!@lingui/loader!@/locales/${initialLocale}.po`);
    i18n.load(initialLocale, lang.messages);
    i18n.activate(initialLocale);
  }, []);

  useEffect(() => {
    activate(importLocale(currentLocale));
    if (currentLanguage) {
      countries.registerLocale(
        require(`i18n-iso-countries/langs/${currentLanguage}.json`)
      );
    }
  }, [currentLocale]);

  return (
    <Context.Provider
      value={{
        locale: currentLocale,
        language: currentLanguage,
        languages,
        currentLanguageName: languages[currentLocale],
        activate: (locale: NormalisedLocale) => {
          setCurrentLocale(locale);
          onActivate?.(locale);
          write('localStorage', 'preferred_locale', locale);
        },
      }}
    >
      <LinguiProvider i18n={i18n}>{children}</LinguiProvider>
    </Context.Provider>
  );
};

export default I18nProvider;

export const withI18nContext =
  <T, K extends { locale?: string }>(HeadComponent: HeadFC<T, K>) =>
  ({ data, pageContext, ...rest }: HeadProps<T, K>) => {
    return (
      <I18nProvider locale={pageContext?.locale as NormalisedLocale | undefined}>
        <HeadComponent {...rest} data={data} pageContext={pageContext} />
      </I18nProvider>
    );
  };
